/**
 * Ads.jsx
 * The ad account a brand spends from, and everything the app has put money behind.
 *
 * The screen is arranged around one question — *what is this costing me?* —
 * because that is the thing you cannot get back. Committed spend and the cap
 * sit above everything else, and the only button that starts money moving is
 * deliberately not the primary action on any row.
 *
 * Adverts and boosts get a section each. They share a table and every money
 * control, but they are not the same product and filing them together made the
 * builder invisible: an advert carries its own image, headline and audience and
 * optimises for clicks, installs or sign-ups, while a boost only pushes a post
 * that is already live and optimises for engagement.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { Check, Database, Megaphone, Plus, Refresh, Warning } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';
import DeliveryCheck from '../components/ads/DeliveryCheck.jsx';
import ConnectionCheck from '../components/ads/ConnectionCheck.jsx';

const money = (pence) => `£${((pence ?? 0) / 100).toFixed(2)}`;

const STATUS = {
  paused: { label: 'Paused', className: 'badge--accent' },
  scheduled: { label: 'Scheduled', className: 'badge--accent' },
  active: { label: 'Running', className: 'badge--ok' },
  finished: { label: 'Finished', className: '' },
  failed: { label: 'Failed', className: 'badge--danger' },
  cancelled: { label: 'Cancelled', className: '' },
};

/** Approved but not yet begun: Meta is holding it until its start time. */
const startsLater = (row) =>
  Boolean(row.starts_at) && new Date(row.starts_at).getTime() > Date.now();

// Mirrors ADS.objectives in the Worker config. Only used to label a row that
// already exists, so a value the Worker adds later degrades to its raw key
// rather than to nothing.
const OBJECTIVE = {
  traffic: 'Clicks to the website',
  installs: 'App installs',
  conversions: 'Sign-ups on the website',
};

export default function Ads() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState(null);
  const [boosts, setBoosts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [committed, setCommitted] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const [acc, bo, { brands: bs }] = await Promise.all([
        api.ads.accounts(),
        api.ads.boosts(),
        api.brands.list(),
      ]);
      setAccounts(acc);
      setBoosts(bo.boosts);
      setCommitted(bo.committedPence);
      setSettings(bo.settings);
      setBrands(bs);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function attach(brandId, actId) {
    setBusy(true);
    try {
      const res = await api.ads.attach(brandId, actId);
      setNote(res.warning ?? 'Ad account attached.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function start(boost) {
    const total = boost.daily_budget_pence * boost.duration_days;
    const what = boost.kind === 'ad' ? 'advert' : 'boost';
    const later = startsLater(boost);

    const cost =
      `It will spend up to ${money(boost.daily_budget_pence)} a day for ` +
      `${boost.duration_days} days — ${money(total)} in total — on ${boost.brand_name}'s ` +
      `ad account.\n\nThis is real money.`;

    // Approving something that begins next Tuesday is a different decision
    // from starting one now, so it is not described as though it were the
    // same. The money is committed either way, which is the part to say.
    const question = later
      ? `Approve this ${what} to start on its own?\n\nIt begins ` +
        `${formatLondon(boost.starts_at)} and nothing further is needed from you — Meta starts ` +
        `it at that moment.\n\n${cost}`
      : `Start this ${what}?\n\n${cost}`;

    if (!confirm(question)) return;

    setBusy(true);
    try {
      const res = await api.ads.start(boost.id);
      const noun = what === 'advert' ? 'Advert' : 'Boost';
      setNote(
        res.status === 'scheduled'
          ? `${noun} approved. It starts ${formatLondon(res.startsAt ?? boost.starts_at)}.`
          : `${noun} started. It is now spending.`,
      );
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function pause(boost) {
    setBusy(true);
    try {
      await api.ads.pause(boost.id);
      // The derived status, not the stored one: a scheduled advert whose start
      // time has passed is running, and telling someone it "will not start"
      // after they have just stopped it spending would be wrong.
      setNote(
        (boost.effective_status ?? boost.status) === 'scheduled'
          ? 'Cancelled. It will not start, and the budget is back under the cap.'
          : boost.kind === 'ad'
            ? 'Advert paused.'
            : 'Boost paused.',
      );
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(boost) {
    setBusy(true);
    try {
      await api.ads.dismiss(boost.id);
      setNote('Attempt cleared.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function dismissAllFailed() {
    if (
      !confirm(
        'Clear every failed attempt?\n\nNone of them ever ran, and none of them spent anything.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const { dismissed } = await api.ads.dismissAllFailed();
      setNote(`Cleared ${dismissed} failed attempt${dismissed === 1 ? '' : 's'}.`);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="mono">Loading…</p>;

  const cap = settings?.capPence ?? 0;
  const room = Math.max(0, cap - committed);
  const attachedByBrand = Object.fromEntries((accounts?.attached ?? []).map((a) => [a.brand_id, a]));

  // One table, two products. Rows written before `kind` existed are boosts.
  const adverts = boosts.filter((b) => b.kind === 'ad');
  const postBoosts = boosts.filter((b) => b.kind !== 'ad');
  const liveState = (b) => b.effective_status ?? b.status;
  const scheduledCount = boosts.filter((b) => liveState(b) === 'scheduled').length;
  const failedCount = boosts.filter((b) => b.status === 'failed').length;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Ads</div>
          <h1>Adverts and boosts</h1>
          <p>
            Build a proper Facebook and Instagram advert below, or boost a post that is already
            live. Everything arrives paused; nothing spends until you start it, and Meta bills the
            ad account directly.
          </p>
        </div>
        <div className="page-head__actions">
          <button className="btn" onClick={load} disabled={busy}>
            <Refresh /> Refresh
          </button>
          <button className="btn btn--primary" onClick={() => navigate('/ads/new')}>
            <Plus /> Build an advert
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {note && (
        <div className="notice notice--info">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {accounts?.problem && (
        <div className="notice notice--error">
          <Warning />
          <span>{accounts.problem}</span>
        </div>
      )}

      {/* When it works, say so — the permission dance is confusing enough that
          "no error" is not the same as "confirmed working". */}
      {!accounts?.problem && accounts?.missingScopes?.length === 0 && (
        <div className="notice notice--info">
          <Check />
          <span>
            Ads permissions granted, and {accounts.available.length} ad account
            {accounts.available.length === 1 ? '' : 's'} visible.
          </span>
        </div>
      )}

      {/* ---------- What this is costing ---------- */}
      <div className="stat-row">
        <div className="stat">
          <div className="stat__value">{money(committed)}</div>
          <div className="stat__label">Committed</div>
          <div className="stat__hint">Running boosts, for their full run</div>
        </div>
        <div className="stat">
          <div className="stat__value">{money(room)}</div>
          <div className="stat__label">Left this month</div>
          <div className="stat__hint">Of a {money(cap)} cap, set in Settings</div>
        </div>
        <div className="stat">
          <div className="stat__value">{boosts.filter((b) => liveState(b) === 'active').length}</div>
          <div className="stat__label">Running now</div>
          {scheduledCount > 0 && (
            <div className="stat__hint">
              {scheduledCount} scheduled, counted in the committed figure
            </div>
          )}
        </div>
      </div>

      {/* ---------- Ad accounts ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Ad accounts</h2>
            <p>{sharedAccountNote(accounts?.attached ?? [])}</p>
          </div>
        </div>

        <div className="rows">
          {brands.map((b) => {
            const attached = attachedByBrand[b.id];
            return (
              <div className="row" key={b.id}>
                <span className={`tile tile--sm ${attached ? 'tile--ok' : 'tile--warn'}`}>
                  <Database size={17} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row__label">{b.name}</div>
                  <div className="row__sub">
                    {attached
                      ? `${attached.display_name} · ${attached.act_id} · ${attached.currency}`
                      : 'No ad account attached'}
                  </div>
                </div>
                <div className="row__end">
                  {attached ? (
                    <button
                      className="btn btn--sm btn--danger"
                      onClick={async () => {
                        if (!confirm(`Detach the ad account from ${b.name}?`)) return;
                        await api.ads.detach(b.id);
                        await load();
                      }}
                      disabled={busy}
                    >
                      Detach
                    </button>
                  ) : (
                    <select
                      defaultValue=""
                      disabled={busy || !(accounts?.available ?? []).length}
                      onChange={(e) => e.target.value && attach(b.id, e.target.value)}
                    >
                      <option value="">
                        {(accounts?.available ?? []).length ? 'Choose an account…' : 'None available'}
                      </option>
                      {(accounts?.available ?? []).map((a) => (
                        <option key={a.actId} value={a.actId}>
                          {a.name} ({a.currency}){a.active ? '' : ' — inactive'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Meta's own answer to "where are my adverts?" — asked, not assumed. */}
        <ConnectionCheck />
      </section>

      {/* ---------- What ads optimise towards ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>What ads optimise towards</h2>
            <p>
              An install ad needs the app; a sign-up ad needs a pixel. Without them Meta accepts
              the ad and spends the budget with nothing to aim at.
            </p>
          </div>
        </div>

        <div className="rows">
          {brands.map((b) => (
            <AdAssets key={b.id} brand={b} onChanged={load} onError={setError} />
          ))}
        </div>
      </section>

      {/* ---------- Adverts ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Adverts</h2>
            <p>
              A real Facebook and Instagram advert: your own image, headline and button, aimed at
              an audience you choose, optimised for clicks, installs or sign-ups. It does not need
              a post to exist first.
            </p>
          </div>
          <div className="card__head-end">
            {/* Only offered when there is more than one to clear — a single
                failed row has its own Dismiss button on the row itself. */}
            {failedCount > 1 && (
              <button className="btn" onClick={dismissAllFailed} disabled={busy}>
                Clear {failedCount} failed
              </button>
            )}
            <button className="btn btn--primary" onClick={() => navigate('/ads/new')}>
              <Plus /> Build an advert
            </button>
          </div>
        </div>

        {adverts.length === 0 ? (
          <div className="empty">
            <div className="empty__art">
              <Megaphone size={30} />
            </div>
            <h3>No adverts yet</h3>
            <p>
              An advert is not a boost. A boost pushes a post that already exists and buys
              engagement; an advert carries its own creative and can optimise for what you
              actually want. Build one and it arrives here, paused, for you to start.
            </p>
            <button className="btn btn--primary" onClick={() => navigate('/ads/new')}>
              <Plus /> Build an advert
            </button>
          </div>
        ) : (
          <div className="rows">
            {adverts.map((b) => (
              <SpendRow
                key={b.id}
                row={b}
                busy={busy}
                onStart={start}
                onPause={pause}
                onDismiss={dismiss}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------- Boosts ---------- */}
      <section className="card">
        <div className="card__head">
          <div className="card__head-text">
            <h2>Boosts</h2>
            <p>
              Created from the Analytics screen, on a post that is already live. Cheaper to set up
              and good for pushing something that is already working — but it optimises for
              engagement, not for people who convert.
            </p>
          </div>
        </div>

        {postBoosts.length === 0 ? (
          <div className="empty">
            <div className="empty__art">
              <Database size={30} />
            </div>
            <h3>Nothing boosted yet</h3>
            <p>
              Boosts start on the Analytics screen: find a Facebook post worth more people seeing
              and press <strong>Boost</strong>. It comes back here, paused, for you to start.
            </p>
            <button className="btn" onClick={() => navigate('/analytics')}>
              Find a post to boost
            </button>
          </div>
        ) : (
          <div className="rows">
            {postBoosts.map((b) => (
              <SpendRow
                key={b.id}
                row={b}
                busy={busy}
                onStart={start}
                onPause={pause}
                onDismiss={dismiss}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/**
 * One thing the app has put money behind, advert or boost.
 *
 * Shared because the money side is identical — the same budget, run length,
 * cap and pause controls — and only the middle line differs: an advert is
 * described by what it optimises for and what it says, a boost by the post it
 * is pushing.
 */
function SpendRow({ row, busy, onStart, onPause, onDismiss }) {
  const [asking, setAsking] = useState(false);
  // What it is doing now, not what was last written. A scheduled advert starts
  // at Meta on its own, so the stored status lags until the nightly run.
  const state = row.effective_status ?? row.status;
  const status = STATUS[state] ?? { label: state, className: '' };
  const total = row.daily_budget_pence * row.duration_days;
  const isAd = row.kind === 'ad';

  let creative = null;
  if (isAd && row.creative_json) {
    try {
      creative = JSON.parse(row.creative_json);
    } catch {
      // A row that cannot be parsed still shows its money, which is the part
      // that matters.
      creative = null;
    }
  }

  return (
    <div className="row" style={{ flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row__label">
          {row.brand_name} · {money(row.daily_budget_pence)}/day for {row.duration_days} days
        </div>

        {isAd ? (
          <>
            <div className="row__sub">
              {row.name || creative?.headline || 'Untitled advert'}
              {row.objective ? ` · ${OBJECTIVE[row.objective] ?? row.objective}` : ''}
            </div>
            <div className="row__sub">{describeAdCreative(creative)}</div>
          </>
        ) : (
          <div className="row__sub">
            {row.caption ? `${row.caption.slice(0, 80)}…` : row.object_story_id}
          </div>
        )}

        <div className="row__sub">
          Up to {money(total)} ·{' '}
          {state === 'scheduled'
            ? `starts ${formatLondon(row.starts_at)}`
            : `ends ${formatLondon(row.ends_at)}`}
          {row.last_error ? ` · ${row.last_error}` : ''}
        </div>
      </div>

      <div className="row__end">
        <span className={`badge ${status.className}`}>
          {state === 'active' && <Check size={13} />}
          {status.label}
        </span>

        {/* A paused advert with a start time in the future is not started, it
            is approved — Meta does the waiting. Saying "Start spending" would
            promise something that does not happen for another week. */}
        {state === 'paused' && (
          <button className="btn btn--sm" onClick={() => onStart(row)} disabled={busy}>
            {startsLater(row) ? 'Approve for its start time' : 'Start spending'}
          </button>
        )}
        {(state === 'active' || state === 'scheduled') && (
          <button className="btn btn--sm btn--primary" onClick={() => onPause(row)} disabled={busy}>
            {state === 'scheduled' ? 'Cancel' : 'Pause'}
          </button>
        )}

        {/* Only a failed attempt can go. It never ran, so there is no record of
            spending in it to lose. */}
        {state === 'failed' && onDismiss && (
          <button className="btn btn--sm" onClick={() => onDismiss(row)} disabled={busy}>
            Dismiss
          </button>
        )}

        {/* Anything Meta actually built can be asked about. */}
        {row.campaign_id && !asking && (
          <button className="btn btn--sm" onClick={() => setAsking(true)} disabled={busy}>
            Ask Meta
          </button>
        )}
      </div>

      {asking && (
        <div style={{ flexBasis: '100%', marginTop: 12 }}>
          <DeliveryCheck boostId={row.id} onClose={() => setAsking(false)} />
        </div>
      )}
    </div>
  );
}

/**
 * The app and pixel one brand advertises with.
 *
 * Listed from Meta rather than typed in: an application id or pixel id entered
 * by hand cannot be checked, and a wrong one silently ruins the optimisation
 * while still spending the budget.
 */
function AdAssets({ brand, onChanged, onError }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.ads.assets(brand.id));
    } catch (e) {
      onError(e);
    }
  }, [brand.id, onError]);

  useEffect(() => {
    if (open && !data) load();
  }, [open, data, load]);

  async function attach(kind, value) {
    if (!value) return;
    setBusy(true);
    try {
      if (kind === 'app') {
        const app = data.apps.find((a) => a.id === value);
        const storeUrl = app?.storeUrls?.google_play ?? app?.storeUrls?.itunes ?? null;
        if (!storeUrl) {
          onError(
            new Error(
              `${app?.name ?? 'That app'} has no store URL registered with Meta, so an install ad ` +
                'would have nowhere to send people. Add one in the Meta app settings first.',
            ),
          );
          return;
        }
        await api.ads.attachAsset({
          brand_id: brand.id,
          kind: 'app',
          asset_id: app.id,
          display_name: app.name,
          store_url: storeUrl,
        });
      } else {
        const pixel = data.pixels.find((x) => x.id === value);
        await api.ads.attachAsset({
          brand_id: brand.id,
          kind: 'pixel',
          asset_id: pixel.id,
          display_name: pixel.name,
        });
      }
      await load();
      await onChanged();
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row" style={{ alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row__label">{brand.name}</div>
        <div className="row__sub">
          App: {data?.attached?.app?.display_name ?? 'none'} · Pixel:{' '}
          {data?.attached?.pixel?.display_name ?? 'none'}
        </div>

        {open && data && (
          <div className="field--row" style={{ marginTop: 10 }}>
            <div>
              <label>App (for install ads)</label>
              <select
                defaultValue=""
                disabled={busy}
                onChange={(e) => attach('app', e.target.value)}
              >
                <option value="">{data.apps.length ? 'Choose an app…' : 'No apps found'}</option>
                {data.apps.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>Pixel (for sign-up ads)</label>
              <select
                defaultValue=""
                disabled={busy}
                onChange={(e) => attach('pixel', e.target.value)}
              >
                <option value="">
                  {data.pixels.length ? 'Choose a pixel…' : 'No pixels on this ad account'}
                </option>
                {data.pixels.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {open && data?.problem && <p className="hint hint--error">{data.problem}</p>}
      </div>

      <div className="row__end">
        <button className="btn btn--sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Done' : 'Change'}
        </button>
      </div>
    </div>
  );
}

/**
 * What an advert is actually made of, in one line.
 *
 * Says which platforms a borrowed-post advert covers, because a pair and a
 * single look identical on this row otherwise and only one of them is running
 * on Instagram.
 *
 * Tolerates the older single-`post` shape, so adverts created before pairs
 * existed still describe themselves.
 */
function describeAdCreative(creative) {
  if (!creative) return 'Own image and words';

  const posts = creative.posts ?? (creative.post ? [creative.post] : []);
  if (creative.source === 'post' && posts.length) {
    const where = posts
      .map((p) => (p.platform === 'instagram' ? 'Instagram' : 'Facebook'))
      .join(' and ');
    return posts.length > 1
      ? `Two live posts — ${where}, on one budget`
      : `A live ${where} post`;
  }

  return creative.body ? `${creative.body.slice(0, 80)}…` : 'Own image and words';
}

/**
 * Two brands may legitimately point at the same ad account. When they do, the
 * app still stops you boosting the wrong brand's post — but the money comes
 * out of one pot, and saying otherwise would be misleading.
 */
function sharedAccountNote(attached) {
  const ids = new Set(attached.map((a) => a.act_id));
  if (attached.length > 1 && ids.size === 1) {
    return 'Both brands share one ad account, so all boosts are billed to the same place.';
  }
  return 'One per brand. A boost only ever promotes a post belonging to that brand.';
}
